import * as signalR from '@microsoft/signalr';
import toast from 'react-hot-toast';
import { logSignalRMessage } from './signalRMessageLogger';

const STORAGE_KEY_ACTIVE_GAME_GROUPS = 'signalr_active_game_groups';
const STORAGE_KEY_ACTIVE_GAME_SPACE_GROUPS = 'signalr_active_game_space_groups';
const GAME_SPACE_GROUP_PREFIX = 'space_';

// Define interfaces for the service
interface ChatMessage {
  gameId?: number;
  userId: string;
  userName: string;
  message: string;
  timestamp: Date;
}

interface UserConnection {
  userId: string;
  userName: string;
  connectionId: string;
  isConnected: boolean;
  lastConnected: Date;
  lastDisconnected?: Date;
}

interface UserConnectionStatus {
  userId: string;
  userName?: string;
  connectionId?: string;
  isConnected: boolean;
  timestamp: Date;
}

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private readonly hubUrl = import.meta.env.VITE_SIGNALR_HUB_URL || '/gamehub';
  private connectedUsers: Map<string, UserConnection> = new Map();
  private activeGameGroups: Map<string, string> = new Map(); // Map of gameCode -> displayName

  /**
   * Connect to the SignalR hub
   */
  async connect(): Promise<void> {
    // Load previously active game groups
    this.loadActiveGameGroups();
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      console.log('[SignalR] Already connected, skipping connection');
      return;
    }

    console.log('[SignalR] Initializing connection to hub:', this.hubUrl);
    const token = localStorage.getItem('token');
    console.log('[SignalR] Auth token available:', !!token);
    
    // Check if token is valid (not expired) by checking if it's a valid JWT format
    if (token) {
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          console.warn('[SignalR] Token does not appear to be a valid JWT format');
        } else {
          // Decode the payload (middle part) to check expiration
          const payload = JSON.parse(atob(tokenParts[1]));
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            console.warn('[SignalR] Token appears to be expired. Exp:', new Date(payload.exp * 1000));
          } else {
            console.log('[SignalR] Token validation passed. User ID:', payload.sub || payload.nameid);
          }
        }
      } catch (e) {
        console.warn('[SignalR] Error parsing token:', e);
      }
    }
    
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => token || '',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Debug) // Enable detailed logging
      .build();

    // Add connection state change handler for debugging
    this.connection.onclose((error) => {
      console.log('[SignalR] Connection closed', error ? `with error: ${error}` : 'without errors');
      toast.error('Connection to game server lost');
    });

    this.connection.onreconnecting((error) => {
      console.log('[SignalR] Attempting to reconnect', error);
      toast.loading('Reconnecting to game server...');
    });

    this.connection.onreconnected((connectionId) => {
      console.log('[SignalR] Reconnected with ID:', connectionId);
      toast.success('Reconnected to game server');
      
      // Rejoin all active game groups
      this.rejoinActiveGameGroups();
    });

    try {
      console.log('[SignalR] Starting connection...');
      await this.connection.start();
      console.log('[SignalR] Connected successfully with ID:', this.connection.connectionId);
    } catch (error: any) {
      console.error('[SignalR] Connection failed:', error);
      // Enhanced error reporting
      if (error.message) {
        console.error('[SignalR] Error message:', error.message);
      }
      if (error.statusCode) {
        console.error('[SignalR] Status code:', error.statusCode);
      }
      throw error;
    }
  }

  /**
   * Disconnect from the SignalR hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        console.log('[SignalR] Stopping connection...');
        await this.connection.stop();
        console.log('[SignalR] Connection stopped');
        this.connection = null;
      } catch (error) {
        console.error('[SignalR] Disconnection failed:', error);
        throw error;
      }
      this.connection = null;
    }
  }

  /**
   * Send a chat message to a game session
   * @param sessionId The ID of the game session
   * @param message The message to send
   */
  async sendChatMessage(sessionId: number, message: string): Promise<void> {
    if (!this.connection) {
      console.error('[SignalR] Cannot send message: No connection available');
      return;
    }
    
    try {
      console.log(`[SignalR] Sending chat message to game: ${sessionId}`);
      await this.connection.invoke('SendChatMessage', sessionId, message);
      console.log(`[SignalR] Successfully sent chat message to game: ${sessionId}`);
      toast.success(`Chat message sent to game: ${sessionId}`);
    } catch (error) {
      console.error('[SignalR] Failed to send chat message:', error);
      throw error;
    }
  }

  /**
   * Register a callback for when a chat message is received
   * @param callback Function to call when a chat message is received
   */
  onChatMessage(callback: (message: ChatMessage) => void): void {
    if (!this.connection) {
      console.warn('[SignalR] Cannot register onChatMessage: No connection available');
      return;
    }

    console.log('[SignalR] Registering chat message handler');
    this.connection?.on('ChatMessage', (message: ChatMessage) => {
      // Log message to the central logger
      logSignalRMessage('ChatMessage', message);
      console.log('[SignalR] Chat message received:', message);
      callback(message);
    });
  }

  /**
   * Register a callback for when an error message is received
   * @param callback Function to call when an error message is received
   */
  onError(callback: (message: string) => void): void {
    console.log('[SignalR] Registering Error event handler');
    this.connection?.on('Error', (message: string) => {
      // Log to the central logger
      logSignalRMessage('Error', message);
      console.log('[SignalR] Error event received:', message);
      // Show toast notification for testing
      toast.error(`SignalR Error: ${message}`);
      callback(message);
    });
  }

 
  /**
   * Register a callback for when the timer is updated
   * @param callback Function to call when the timer is updated
   */
  onUpdateTimer(callback: (timeRemaining: number) => void): void {
    console.log('[SignalR] Registering UpdateTimer event handler');
    this.connection?.on('updatetimer', (timeRemaining: number) => {
      // Log to the central logger
      logSignalRMessage('updatetimer', timeRemaining);
      console.log('[SignalR] UpdateTimer event received:', timeRemaining);
      // Show toast notification for testing
      toast.success(`Timer updated: ${timeRemaining} seconds remaining`);
      callback(timeRemaining);
    });
  }

  /**
   * Register a callback for when a question has ended
   * @param callback Function to call when a question has ended
   */
  onQuestionEnded(callback: (data: any) => void): void {
    console.log('[SignalR] Registering QuestionEnded event handler');
    this.connection?.on('QuestionEnded', (data: any) => {
      // Log to the central logger
      logSignalRMessage('QuestionEnded', data);
      console.log('[SignalR] QuestionEnded event received:', data);
      // Show toast notification for testing
      toast.success(`Question ended`);
      callback(data);
    });
  }
 

  /**
   * Register a callback for when the game has ended
   * @param callback Function to call when the game has ended
   */
  onGameEnded(callback: (data: any) => void): void {
    console.log('[SignalR] Registering GameEnded event handler');
    this.connection?.on('GameEnded', (data: any) => {
      // Log to the central logger
      logSignalRMessage('GameEnded', data);
      console.log('[SignalR] GameEnded event received:', data);
      // Show toast notification for testing
      toast.success(`Game ended`);
      callback(data);
    });
  }

  /**
   * Register a callback for when a game message is received
   * @param callback Function to call when a game message is received
   */
  onGameMessage(callback: (message: any, type: string) => void): void {
    console.log('[SignalR] Registering GameMessage event handler');
    this.connection?.on('gamemessage', (message: any, type: string) => {
      // Log to the central logger
      logSignalRMessage('gamemessage', { message, type });
      console.log(`[SignalR] GameMessage event received (${type}):`, message);
      // Show toast notification for testing
      toast.success(`Game message (${type}): ${JSON.stringify(message).substring(0, 50)}${JSON.stringify(message).length > 50 ? '...' : ''}`);
      callback(message, type);
    });
  }

  // InstantMultiplayer specific event handlers
  onGameStarted(callback: (data: any) => void): void {
    if (!this.connection) {
      console.warn('SignalRService: Cannot register onGameStarted callback - connection not established');
      return;
    }
    
    console.log('SignalRService: Registering onGameStarted callback');
    this.connection?.on('GameStarted', (data: any) => {
      // Log to the central logger
      logSignalRMessage('GameStarted', data);
      console.log('SignalRService: GameStarted event received', data);
      // Show toast notification for testing
      toast.success(`Game started`);
      callback(data);
    });
  }
  

  /**
   * Register a callback for when moving to next question (handles both NextQuestion and MoveToNextQuestion events)
   * @param callback Function to call when moving to next question
   */
 onNextQuestion(callback: (question: any) => void): void {
  console.log('[SignalR] Registering MoveToNextQuestion event handlers');
  
    this.connection?.on('MoveToNextQuestion', (data: any) => {
    // Log to the central logger
    logSignalRMessage('MoveToNextQuestion', data);
    console.log('[SignalR] MoveToNextQuestion event received:', data);
    // Show toast notification for testing
    toast.success(`Moving to next question`);
    callback(data);
  });
}



  /**
   * Get the current connection state
   */
  get connectionState(): signalR.HubConnectionState | null {
    return this.connection?.state || null;
  }

  /**
   * Invoke a hub method - for testing purposes
   * @param methodName The name of the hub method to invoke
   * @param args Arguments to pass to the hub method
   */
  async invokeHub(methodName: string, ...args: any[]): Promise<any> {
    if (!this.connection) {
      throw new Error('SignalR not connected');
    }
    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error(`SignalR not in connected state: ${this.connection.state}`);
    }
    return this.connection.invoke(methodName, ...args);
  }

  /**
   * Manually reconnect to the SignalR hub if disconnected
   */
  async reconnect(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Disconnected) {
      try {
        await this.connection.start();
        console.log('[SignalR] Reconnected successfully');
      } catch (error) {
        console.error('[SignalR] Reconnection failed:', error);
        throw error;
      }
    }
  }
  
  /**
   * Join a game group to receive updates for that game
   * @param gameCode The game code of the session
   */
  async joinGameGroup(gameCode: string, displayName?: string): Promise<boolean> {
    if (!this.connection) {
      console.error('[SignalR] Cannot join game group: No connection available');
      return false;
    }
    
    try {
      // Verify parameter type
      const gameCodeType = typeof gameCode;
      console.log(`[SignalR] Joining game group: ${gameCode} (type: ${gameCodeType})`);
      
      // Ensure gameCode is a string and not empty
      if (!gameCode || typeof gameCode !== 'string') {
        throw new Error(`Invalid gameCode: ${gameCode} is not a valid string`);
      }
      
      // Log auth status before making the call
      const token = localStorage.getItem('token');
      console.log('[SignalR] Auth token available when joining game:', !!token);
      
      await this.connection.invoke('JoinGameGroupByGameCode', gameCode, displayName);
      console.log(`[SignalR] Successfully joined game group: ${gameCode}`);
      toast.success(`Joined game group: ${gameCode}`);
    } catch (error: any) {
      console.error('[SignalR] Failed to join game group:', error);
      // Enhanced error reporting
      if (error.message) {
        console.error('[SignalR] Error message:', error.message);
      }
      if (error.statusCode) {
        console.error('[SignalR] Status code:', error.statusCode);
      }
      throw error;
    }
  }

  
 
  


/**
 * Check if a user is currently connected
 * @param userId The user ID to check
 * @returns True if the user is connected, false otherwise
 */
isUserConnected(userId: string): boolean {
  const user = this.connectedUsers.get(userId);
  return user ? user.isConnected : false;
}
  
  /**
   * Persist player connection status to the database
   * @param userId The ID of the user
   * @param isConnected Whether the user is connected
   */
  private persistConnectionStatus(userId: string, isConnected: boolean): void {
    // Find the active game session ID for the current connection
    // We'll use the first active game code we find
    if (this.activeGameGroups.size === 0) {
      console.log('[SignalR] No active game groups to persist connection status for');
      return;
    }
    
    import('./gameService').then(gameServiceModule => {
      const gameService = gameServiceModule.default;
      
      // Use the first active game group (most users will only be in one at a time)
      const gameCode = Array.from(this.activeGameGroups.keys())[0];
      console.log(`[SignalR] Persisting connection status for user ${userId} in game ${gameCode}: ${isConnected ? 'connected' : 'disconnected'}`);
      
      // Call the API to persist the connection status
      gameService.updatePlayerConnectionStatus(gameCode, userId, isConnected)
        .then(response => {
          if (response.success) {
            console.log(`[SignalR] Successfully persisted connection status for user ${userId}`);
          } else {
            console.error(`[SignalR] Failed to persist connection status for user ${userId}:`, response.message);
          }
        })
        .catch(error => {
          console.error(`[SignalR] Error persisting connection status for user ${userId}:`, error);
        });
    });
  }
  
  /**
   * Load active game groups from localStorage
   * @private
   */
  private loadActiveGameGroups(): void {
    try {
      const storedGroups = localStorage.getItem('activeGameGroups');
      if (storedGroups) {
        this.activeGameGroups = new Map(JSON.parse(storedGroups));
        console.log('[SignalR] Loaded active game groups from storage:', Array.from(this.activeGameGroups.entries()));
      }
    } catch (error) {
      console.error('[SignalR] Failed to load active game groups from storage:', error);
    }
  }

  /**
   * Save active game groups to localStorage
   * @private
   */
  private saveActiveGameGroups(): void {
    try {
      localStorage.setItem('activeGameGroups', JSON.stringify(Array.from(this.activeGameGroups.entries())));
    } catch (error) {
      console.error('[SignalR] Failed to save active game groups to storage:', error);
    }
  }

  /**
   * Join a GameSpace group to receive updates for that GameSpace
   * @param gameSpaceCode The GameSpace code
   * @param displayName Optional display name for the user
   * @returns A promise that resolves to true if successful, false otherwise
   */
  async joinGameSpaceGroup(gameSpaceCode: string, displayName?: string): Promise<boolean> {
    if (!this.connection) {
      console.error('[SignalR] Cannot join GameSpace group - no connection');
      return false;
    }
    try {
      await this.connection.invoke('JoinGameSpaceGroup', gameSpaceCode, displayName || '');
      // Store in active game groups with a prefix to distinguish from regular game groups
      this.activeGameGroups.set(`gs_${gameSpaceCode}`, displayName || '');
      this.saveActiveGameGroups();
      return true;
    } catch (error) {
      console.error(`[SignalR] Error joining GameSpace group ${gameSpaceCode}:`, error);
      return false;
    }
  }

  /**
   * Leave a game group
   * @param gameCode The game code of the session
   * @returns A promise that resolves when complete
   */
  async leaveGameGroup(gameCode: string): Promise<boolean> {
    if (!this.connection) {
      console.error('[SignalR] Cannot leave game group - no connection');
      return false;
    }
    try {
      await this.connection.invoke('LeaveGameGroup', gameCode);
      // Remove from active game groups
      this.activeGameGroups.delete(gameCode);
      this.saveActiveGameGroups();
      return true;
    } catch (error) {
      console.error(`[SignalR] Error leaving game group ${gameCode}:`, error);
      return false;
    }
  }

  /**
   * Register a callback for when a user connects
   * @param callback Function to call when a user connects
   */
  onUserConnected(callback: (status: UserConnectionStatus) => void): void {

    console.log('[SignalR] Registering UserConnected event handler');
    
    if (!this.connection) {
      console.warn('[SignalR] Cannot register UserConnected handlers: No connection available');
      return;
    }
    
    
    
    console.log('[SignalR] Registering lowercase userconnected handler');
    this.connection.on('userconnected', (status: UserConnectionStatus) => {
      logSignalRMessage('userconnected', status);
      console.log('[SignalR] userconnected (lowercase) event received:', status);
      // Same logic as above - track this user in our connected users map
      const connection: UserConnection = {
        userId: status.userId,
        userName: status.userName || status.userId,
        connectionId: status.connectionId || '',
        isConnected: status.isConnected,
        lastConnected: status.timestamp,
      };
      
      this.connectedUsers.set(status.userId, connection);
      toast.success(`User connected: ${status.userName || status.userId}`);
      
      // Persist connection status to the database if we have an active game code
      this.persistConnectionStatus(status.userId, true);
      
      callback(status);
    });
  }
  
  /**
   * Register a callback for when a user disconnects
   * @param callback Function to call when a user disconnects
   */
  onUserDisconnected(callback: (status: UserConnectionStatus) => void): void {
    console.log('[SignalR] Registering UserDisconnected event handler');
    
    
    this.connection?.on('userdisconnected', (status: UserConnectionStatus) => {
      logSignalRMessage('userdisconnected', status);
      console.log('[SignalR] userdisconnected (lowercase) event received:', status);
      // Same logic as above
      const existingConnection = this.connectedUsers.get(status.userId);
      if (existingConnection) {
        existingConnection.isConnected = false;
        existingConnection.lastDisconnected = status.timestamp;
        this.connectedUsers.set(status.userId, existingConnection);
      }
      
      // Persist disconnection status to the database
      this.persistConnectionStatus(status.userId, false);
      
      toast.error(`User disconnected: ${status.userName || status.userId}`);
      callback(status);
    });
  }

  /**
   * Register a callback for when connected users list is received
   * @param callback Function to call when connected users list is received
   */
  onConnectedUsers(callback: (users: UserConnectionStatus[]) => void): void {
    console.log('[SignalR] Registering ConnectedUsers event handler');
    this.connection?.on('connectedusers', (users: UserConnectionStatus[]) => {
      // Log to the central logger
      logSignalRMessage('connectedusers', users);
      console.log('[SignalR] Connected users received:', users);
      
      // Update our local user map
      for (const user of users) {
        const connection: UserConnection = {
          userId: user.userId,
          userName: user.userName || user.userId,
          connectionId: user.connectionId || '',
          isConnected: user.isConnected,
          lastConnected: user.timestamp,
          lastDisconnected: user.isConnected ? undefined : user.timestamp
        };
        
        this.connectedUsers.set(user.userId, connection);
      }
      
      callback(users);
    });
  }
 
  /**
   * Get a user's connection status
   * @param userId The ID of the user to check
   * @returns true if connected, false if not
   */
  getConnectedUsers(userId: string): boolean {
    const user = this.connectedUsers.get(userId);
    return user ? user.isConnected : false;
  }
  
  // Note: The event handlers for UserConnected, UserDisconnected, and ConnectedUsers
  // are already implemented elsewhere in this class via generic SignalR mechanisms.

  /**
   * Request the list of connected users for a game session
   * @param gameCode The game code of the session
   * @returns A promise that resolves when the request is complete
   */
  async requestConnectedUsers(gameCode: string): Promise<void> {
    if (!this.connection) {
      console.error('[SignalR] Cannot request connected users: No connection available');
      return Promise.reject('No connection available');
    }
    
    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn(`[SignalR] Connection not in Connected state (current: ${this.connection.state}). Cannot request connected users.`);
      return Promise.reject(`Connection not in Connected state: ${this.connection.state}`);
    }
    
    try {
      console.log(`[SignalR] Requesting connected users for session: ${gameCode}`);
      await this.connection.invoke('GetConnectedUsers', gameCode);
      console.log('[SignalR] Connected users request sent');
      return Promise.resolve();
    } catch (error: any) {
      console.error('[SignalR] Failed to request connected users:', error);
      toast.error('Failed to get connected users');
      return Promise.reject(error);
    }
  }

 
  /**
   * Rejoin all active game groups after a reconnection
   */
  async rejoinActiveGameGroups(): Promise<void> {
    console.log('[SignalR] Rejoining active game groups after reconnection');
    if (!this.connection) {
      console.error('[SignalR] Cannot rejoin game groups: No connection available');
      return Promise.reject('No connection available');
    }
    
    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      console.warn(`[SignalR] Connection not in Connected state (current: ${this.connection.state}). Cannot rejoin game groups.`);
      return Promise.reject(`Connection not in Connected state: ${this.connection.state}`);
    }
    
    console.log('[SignalR] Rejoining', this.activeGameGroups.size, 'game groups');
    
    // Join each active game group
    for (const [gameCode, displayName] of this.activeGameGroups.entries()) {
      try {
        console.log(`[SignalR] Rejoining game group: ${gameCode} with display name: ${displayName}`);
        await this.joinGameGroup(gameCode, displayName);
      } catch (error: any) {
        console.error(`[SignalR] Failed to rejoin game group ${gameCode}:`, error);
        toast.error(`Failed to rejoin game group: ${gameCode}`);
      }
    }
    
    console.log('[SignalR] Finished rejoining game groups');
    return Promise.resolve();
  }
  
  /**
   * Register a callback for when a player submits an answer
   * @param callback Function to call when a player submits an answer
   */
  onPlayerAnswered(callback: (data: any) => void): void {
    if (!this.connection) {
      console.warn('[SignalR] Cannot register onPlayerAnswered: No connection available');
      return;
    }

    console.log('[SignalR] Registering PlayerAnswered event handler');
    this.connection?.on('playeranswered', (data: any) => {
      // Log to the central logger
      logSignalRMessage('PlayerAnswered', data);
      console.log('[SignalR] PlayerAnswered event received:', data);
      callback(data);
    });
  }

  /**
   * Register a callback for when an answer is submitted (real-time notification)
   * @param callback Function to call when an answer is submitted
   */
  onAnswerSubmitted(callback: (data: any) => void): void {
    if (!this.connection) {
      console.warn('[SignalR] Cannot register onAnswerSubmitted: No connection available');
      return;
    }

    console.log('[SignalR] Registering answer submitted handler');
   
    this.connection?.on('answersubmitted', (data: any) => {
      logSignalRMessage('answersubmitted', data);
      console.log('[SignalR] answerSubmitted event received:', data);
      callback(data);
    });
  }

  /**
   * Register a callback for when a player joins the game
   * @param callback Function to call when a player joins
   */
  onPlayerJoined(callback: (data: any) => void): void {
    if (!this.connection) {
      console.warn('[SignalR] Cannot register onPlayerJoined: No connection available');
      return;
    }

    console.log('[SignalR] Registering PlayerJoined event handler');
    this.connection?.on('playerjoined', (data: any) => {
      logSignalRMessage('PlayerJoined', data);
      console.log('[SignalR] PlayerJoined event received:', data);
      callback(data);
    });
  }

  /**
   * Remove all event listeners to prevent memory leaks
   */
  removeAllListeners(): void {
    console.log('[SignalR] Removing all event listeners');
    if (!this.connection) {
      console.warn('[SignalR] No connection available to remove listeners from');
      return;
    }

    // Remove standard game event listeners
    this.connection?.off('chatmessage');
    this.connection?.off('error');
    this.connection?.off('updatetimer');
    this.connection?.off('questionended');
    this.connection?.off('gameended');
    this.connection?.off('gamemessage');
    this.connection?.off('connectedusers');
    this.connection?.off('playeranswered');
    this.connection?.off('playerjoined');
    
    // Remove user connection event listeners
 
    this.connection?.off('userconnected'); // Lowercase variant
    this.connection?.off('userdisconnected'); // Lowercase variant
    this.connection?.off('answersubmitted'); // Lowercase variant
    
    // Remove InstantMultiplayer specific event listeners
    this.connection?.off('gamestarted');
    this.connection?.off('movetonextquestion');
    
    console.log('[SignalR] All event listeners removed');
  }
}

// Export singleton instance
export const signalRService = new SignalRService();
export default signalRService;
